/** Client-side tax profile types — keep in sync with backend/src/config/billingTax.ts */

export type BillingTaxType =
  | 'none'
  | 'in_gst'
  | 'au_abn'
  | 'nz_gst'
  | 'sg_gst'
  | 'eu_vat'
  | 'gb_vat'
  | 'ca_bn'
  | 'us_ein'
  | 'ae_trn';

export interface TaxTypeDefinition {
  id: BillingTaxType;
  label: string;
  hint: string;
  countries: string[];
}

export const TAX_TYPE_DEFINITIONS: TaxTypeDefinition[] = [
  { id: 'none', label: 'No tax registration', hint: 'Individual or tax-exempt org', countries: [] },
  {
    id: 'in_gst',
    label: 'GSTIN (India)',
    hint: '15-character GST identification number',
    countries: ['IN'],
  },
  {
    id: 'au_abn',
    label: 'ABN / GST (Australia)',
    hint: 'Australian Business Number',
    countries: ['AU'],
  },
  {
    id: 'nz_gst',
    label: 'GST (New Zealand)',
    hint: 'NZ GST number',
    countries: ['NZ'],
  },
  {
    id: 'sg_gst',
    label: 'GST (Singapore)',
    hint: 'UEN or GST registration',
    countries: ['SG'],
  },
  {
    id: 'gb_vat',
    label: 'VAT (United Kingdom)',
    hint: 'UK VAT registration number',
    countries: ['GB'],
  },
  {
    id: 'eu_vat',
    label: 'VAT (EU)',
    hint: 'EU VAT ID (e.g. DE, FR, NL)',
    countries: [
      'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
      'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
    ],
  },
  {
    id: 'ca_bn',
    label: 'BN / GST (Canada)',
    hint: 'Business number or GST/HST',
    countries: ['CA'],
  },
  {
    id: 'us_ein',
    label: 'EIN (United States)',
    hint: 'Employer Identification Number',
    countries: ['US'],
  },
  {
    id: 'ae_trn',
    label: 'TRN (UAE)',
    hint: 'Tax Registration Number',
    countries: ['AE'],
  },
];

export const BILLING_COUNTRY_OPTIONS = [
  { code: 'US', name: 'United States' },
  { code: 'IN', name: 'India' },
  { code: 'AU', name: 'Australia' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'SG', name: 'Singapore' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'IE', name: 'Ireland' },
  { code: 'OTHER', name: 'Other' },
] as const;

export interface BillingTaxProfile {
  legalName?: string;
  countryCode: string;
  taxType: BillingTaxType;
  taxId?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateRegion?: string;
  postalCode?: string;
  updatedAt?: string;
}

export function taxTypesForCountry(countryCode: string): TaxTypeDefinition[] {
  const cc = countryCode.toUpperCase();
  if (cc === 'OTHER') {
    return TAX_TYPE_DEFINITIONS.filter((t) => t.id === 'none' || t.id === 'eu_vat');
  }
  return TAX_TYPE_DEFINITIONS.filter(
    (t) => t.id === 'none' || t.countries.length === 0 || t.countries.includes(cc),
  );
}

export interface BillingInvoiceRow {
  id: string;
  number: string | null;
  status: string | null;
  currency: string;
  subtotal: number;
  tax: number;
  total: number;
  amountPaid: number;
  amountDue: number;
  created: number;
  periodStart: number | null;
  periodEnd: number | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}
